import { authedFetch } from './api';
import { cleanQuote } from './quote';

// When a conversation passes COMPACT_TRIGGER messages, everything except the
// most recent KEEP_RECENT turns is condensed server-side into a summary. The
// full transcript stays in the UI; only the summary + recent turns are sent
// with each request, so long conversations keep working at a bounded cost.
export const COMPACT_TRIGGER = 30;
export const KEEP_RECENT = 12;

/** Messages that only exist in the UI (crisis cards, AI re-notices). */
function isLocalOnly(message) {
  return Boolean(message.local || message.safetyCard);
}

/**
 * One turn as the server gets it: role and content, and on a person's turn
 * the words it quoted ("Ask about this", utils/quote.js), so AI Rob and the
 * summary know what was asked about. Never a quote on AI Rob's turns.
 */
function historyItem(m) {
  const quote = m.role === 'user' ? cleanQuote(m.quote) : null;
  return quote ? { role: m.role, content: m.content, quote } : { role: m.role, content: m.content };
}

/** Builds the history array to send with a chat request. */
export function buildHistory(messages, compaction) {
  const cleaned = messages
    .filter((m) => !isLocalOnly(m))
    .map(historyItem);
  return cleaned.slice(compaction?.uptoIndex || 0);
}

/**
 * Compacts older history into a summary when the conversation is long enough.
 * Returns the updated compaction state ({ summary, uptoIndex }) or the
 * existing one when no compaction was needed/possible. Never throws.
 */
export async function maybeCompact(messages, compaction) {
  const cleaned = messages
    .filter((m) => !isLocalOnly(m))
    .map(historyItem);

  const startIndex = compaction?.uptoIndex || 0;
  if (cleaned.length - startIndex < COMPACT_TRIGGER) {
    return compaction || null;
  }

  const uptoIndex = cleaned.length - KEEP_RECENT;
  const older = cleaned.slice(startIndex, uptoIndex);
  if (older.length < 6) {
    return compaction || null;
  }

  try {
    const response = await authedFetch('compactChatHistory', {
      history: older,
      historySummary: compaction?.summary || '',
    });
    if (!response.ok) {
      return compaction || null;
    }
    const data = await response.json();
    if (!data?.summary) {
      return compaction || null;
    }
    return { summary: data.summary, uptoIndex };
  } catch (err) {
    console.error('History compaction failed:', err);
    return compaction || null;
  }
}
